import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";

function formatCOP(value) {
  if (value === null || value === undefined || isNaN(value)) return "$ 0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency: "COP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
}

function safeInt(val, fallback = 0) {
  const n = parseInt(val);
  return isNaN(n) ? fallback : n;
}

const VIGENCIA_ACTUAL = new Date().getFullYear();

function formatPeriodo(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  const meses = ["","Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${meses[parseInt(m)]} ${y}`;
}

export default function ConcejoHonorarios() {
  const navigate = useNavigate();

  const [categorias, setCategorias]             = useState([]);
  const [ipcs, setIpcs]                         = useState([]);
  const [calculos, setCalculos]                 = useState([]);
  const [icld, setIcld]                         = useState(null);
  const [honorariosPresup, setHonorariosPresup] = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [error, setError]                       = useState(null);
  const [guardando, setGuardando]               = useState(false);
  const [mensajeOk, setMensajeOk]               = useState("");

  const [vigencia, setVigencia]           = useState(VIGENCIA_ACTUAL);
  const [categoria, setCategoria]         = useState("Tercera");
  const [numConcejales, setNumConcejales] = useState(15);
  const [sesOrd, setSesOrd]               = useState(80);
  const [sesExt, setSesExt]               = useState(40);
  const [sesExtInput, setSesExtInput]     = useState("40");

  const [mostrarIpc, setMostrarIpc]   = useState(false);
  const [nuevaVigIpc, setNuevaVigIpc] = useState(VIGENCIA_ACTUAL + 1);
  const [nuevoIpc, setNuevoIpc]       = useState("");

  const cargarDatos = () => {
    setLoading(true);
    fetch(apiConfig.getUrl("/api/hacienda/concejo-honorarios/"), { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setCategorias(data.categorias || []);
        setIpcs(data.ipcs || []);
        setCalculos(data.calculos || []);
        setIcld(data.icld || null);
        setHonorariosPresup(data.honorarios_presupuestados || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargarDatos(); }, []);

  useEffect(() => {
    const cat = categorias.find(c => c.categoria === categoria);
    if (cat) {
      setSesOrd(parseInt(cat.sesiones_ordinarias));
      const extVal = parseInt(cat.sesiones_extraordinarias);
      setSesExt(extVal);
      setSesExtInput(String(extVal));
    }
  }, [categoria, categorias]);

  const calcularValorSesion = () => {
    const cat = categorias.find(c => c.categoria === categoria);
    if (!cat) return 0;
    let valor = parseFloat(cat.valor_sesion);
    for (let v = 2026; v <= vigencia; v++) {
      const ipcReg = ipcs.find(i => parseInt(i.vigencia) === v);
      if (ipcReg && parseFloat(ipcReg.ipc_pct) > 0) {
        valor = valor * (1 + parseFloat(ipcReg.ipc_pct) / 100);
      }
    }
    return Math.round(valor);
  };

  const valorSesion       = calcularValorSesion();
  const sesExtVal         = safeInt(sesExtInput, 0);
  const totalSesiones     = safeInt(sesOrd, 0) + sesExtVal;
  const totalOrd          = valorSesion * safeInt(numConcejales, 0) * safeInt(sesOrd, 0);
  const totalExt          = valorSesion * safeInt(numConcejales, 0) * sesExtVal;
  const totalVig          = totalOrd + totalExt;
  const limiteBasePpto    = icld ? icld.limite_icld_1_5 : 0;
  const honorAprobado     = honorariosPresup ? honorariosPresup.total : 0;
  const superaPresupuesto = honorAprobado > 0 && totalVig > honorAprobado;
  const diferenciaPpto    = totalVig - honorAprobado;

  const handleGuardarCalculo = async () => {
    setGuardando(true); setMensajeOk("");
    try {
      const res = await fetch(apiConfig.getUrl("/api/hacienda/concejo-honorarios/"), {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accion: "guardar_calculo",
          vigencia, categoria,
          num_concejales: safeInt(numConcejales),
          valor_sesion_vigencia: valorSesion,
          sesiones_ordinarias: safeInt(sesOrd),
          sesiones_extraordinarias: sesExtVal,
          total_honorarios_ordinarias: totalOrd,
          total_honorarios_extraordinarias: totalExt,
          total_honorarios_vigencia: totalVig,
          limite_ley: limiteBasePpto,
        }),
      });
      const data = await res.json();
      if (data.ok) {
        setMensajeOk("Cálculo guardado correctamente ✅");
        cargarDatos();
      } else {
        setMensajeOk("Error: " + (data.error || "desconocido"));
      }
    } catch (e) { setMensajeOk("Error: " + e.message); }
    finally { setGuardando(false); }
  };

  const handleGuardarIpc = async () => {
    if (!nuevoIpc) return;
    try {
      const res = await fetch(apiConfig.getUrl("/api/hacienda/concejo-honorarios/"), {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accion: "guardar_ipc", vigencia: nuevaVigIpc, ipc_pct: parseFloat(nuevoIpc) }),
      });
      const data = await res.json();
      if (data.ok) { setMensajeOk("IPC guardado ✅"); setNuevoIpc(""); cargarDatos(); }
    } catch (e) { setMensajeOk("Error: " + e.message); }
  };

  const card = {
    background: "#fff", borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    padding: "1.5rem", marginBottom: "1.5rem",
  };
  const inputStyle = {
    border: "1px solid #d1d5db", borderRadius: "8px",
    padding: "0.5rem 0.75rem", fontSize: "0.95rem",
    width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontWeight: 600,
    color: "#374151", marginBottom: "0.35rem", fontSize: "0.9rem",
  };

  if (loading) return <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}>Cargando...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "2rem 1.5rem" }}>
      <div style={{ maxWidth: "960px", margin: "0 auto" }}>

        <button type="button" onClick={() => navigate("/hacienda-presupuesto")}
          style={{ background: "#e5e7eb", color: "#111827", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem",
            cursor: "pointer", fontWeight: 600, marginBottom: "1.5rem" }}>
          ← Volver a Hacienda
        </button>

        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ margin: 0, color: "#163b8c", fontSize: "2rem", fontWeight: 800 }}>
            Concejo Municipal
          </h1>
          <p style={{ marginTop: "0.5rem", color: "#6b7280" }}>
            Cálculo de honorarios de concejales — Art. 10 Ley 617/2000
          </p>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5",
          borderRadius: "12px", padding: "1rem", color: "#991b1b", marginBottom: "1rem" }}>⚠️ {error}</div>}

        {mensajeOk && <div style={{
          background: mensajeOk.includes("Error") ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${mensajeOk.includes("Error") ? "#fca5a5" : "#86efac"}`,
          borderRadius: "12px", padding: "1rem",
          color: mensajeOk.includes("Error") ? "#991b1b" : "#166534",
          marginBottom: "1rem", fontWeight: 600 }}>{mensajeOk}</div>}

        {/* ── Parámetros ── */}
        <div style={card}>
          <h2 style={{ margin: "0 0 1.25rem", color: "#163b8c", fontSize: "1.15rem" }}>
            Parámetros del cálculo
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <label style={labelStyle}>Vigencia</label>
              <input type="number" value={vigencia} min={2025} max={2100}
                onChange={e => setVigencia(parseInt(e.target.value))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Categoría municipio</label>
              <select value={categoria} onChange={e => setCategoria(e.target.value)} style={inputStyle}>
                {categorias.map(c => <option key={c.categoria} value={c.categoria}>{c.categoria}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Número de concejales</label>
              <input type="number" value={numConcejales} min={1} max={21}
                onChange={e => setNumConcejales(e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Sesiones ordinarias</label>
              <input type="number" value={sesOrd} min={0}
                onChange={e => setSesOrd(e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Sesiones extraordinarias</label>
              <input type="number" value={sesExtInput} min={0}
                onChange={e => { setSesExtInput(e.target.value); setSesExt(safeInt(e.target.value, 0)); }}
                style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Total sesiones vigencia</label>
              <input type="number" value={isNaN(totalSesiones) ? 0 : totalSesiones} disabled
                style={{ ...inputStyle, background: "#f9fafb", color: "#6b7280" }} />
            </div>
          </div>
        </div>

        {/* ── Cálculo Honorario Concejales ── */}
        <div style={card}>
          <h2 style={{ margin: "0 0 1rem", color: "#163b8c", fontSize: "1.15rem" }}>
            Cálculo Honorario Concejales {vigencia}
          </h2>
          <div style={{ background: "#eff6ff", borderRadius: "10px", padding: "0.85rem 1rem",
            marginBottom: "1.25rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ color: "#1e40af", fontWeight: 600 }}>Valor por sesión {vigencia} ({categoria})</span>
            <span style={{ fontWeight: 800, fontSize: "1.1rem", color: "#1e40af" }}>{formatCOP(valorSesion)}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
            <tbody>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                  Honorarios sesiones ordinarias
                  <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                    {formatCOP(valorSesion)} × {safeInt(numConcejales)} concejales × {safeInt(sesOrd)} sesiones
                  </div>
                </td>
                <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                  {formatCOP(totalOrd)}
                </td>
              </tr>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                  Honorarios sesiones extraordinarias
                  <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                    {formatCOP(valorSesion)} × {safeInt(numConcejales)} concejales × {sesExtVal} sesiones
                  </div>
                </td>
                <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                  {formatCOP(totalExt)}
                </td>
              </tr>
              <tr style={{ background: "#f0f9ff" }}>
                <td style={{ padding: "0.75rem 0.5rem", fontWeight: 700, color: "#163b8c" }}>
                  Total honorarios vigencia
                </td>
                <td style={{ padding: "0.75rem 0.5rem", textAlign: "right",
                  fontWeight: 800, fontSize: "1.15rem", color: "#163b8c" }}>
                  {formatCOP(totalVig)}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Comparación contra presupuesto aprobado */}
          {honorAprobado > 0 && (
            <div style={{ marginTop: "1rem", padding: "0.85rem 1rem", borderRadius: "10px",
              background: superaPresupuesto ? "#fef2f2" : "#f0fdf4",
              border: `1px solid ${superaPresupuesto ? "#fca5a5" : "#86efac"}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontWeight: 600, color: superaPresupuesto ? "#991b1b" : "#166534" }}>
                  {superaPresupuesto ? "⚠️" : "✅"} Presupuesto aprobado honorarios concejales
                </span>
                <span style={{ fontWeight: 700, color: superaPresupuesto ? "#dc2626" : "#16a34a" }}>
                  {formatCOP(honorAprobado)}
                </span>
              </div>
              <div style={{ fontSize: "0.8rem", color: "#6b7280", marginTop: "0.3rem" }}>
                Sección 16: {formatCOP(honorariosPresup?.sec16)} + Sección 18: {formatCOP(honorariosPresup?.sec18)}
              </div>
              {superaPresupuesto ? (
                <div style={{ marginTop: "0.5rem", padding: "0.6rem 0.75rem",
                  background: "#fee2e2", borderRadius: "8px", fontSize: "0.88rem", color: "#991b1b" }}>
                  El cálculo supera el presupuesto aprobado en{" "}
                  <strong>{formatCOP(diferenciaPpto)}</strong>.
                  Se debe adicionar este valor al presupuesto del concejo para cubrir los honorarios.
                </div>
              ) : (
                <div style={{ marginTop: "0.5rem", padding: "0.6rem 0.75rem",
                  background: "#dcfce7", borderRadius: "8px", fontSize: "0.88rem", color: "#166534" }}>
                  El cálculo está dentro del presupuesto aprobado. Hay un margen disponible de{" "}
                  <strong>{formatCOP(Math.abs(diferenciaPpto))}</strong>.
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: "1.25rem" }}>
            <button type="button" onClick={handleGuardarCalculo} disabled={guardando}
              style={{ background: "#1d4ed8", color: "#fff", border: "none",
                borderRadius: "10px", padding: "0.75rem 1.5rem",
                cursor: guardando ? "not-allowed" : "pointer",
                fontWeight: 600, opacity: guardando ? 0.7 : 1 }}>
              {guardando ? "Guardando..." : "💾 Guardar cálculo"}
            </button>
          </div>
        </div>

        {/* ── Seguimiento Límite ICLD Concejo ── */}
        {icld && (
          <div style={card}>
            <h2 style={{ margin: "0 0 0.5rem", color: "#163b8c", fontSize: "1.15rem" }}>
              Seguimiento Límite ICLD Concejo
            </h2>
            <p style={{ margin: "0 0 1.25rem", fontSize: "0.85rem", color: "#6b7280" }}>
              Periodo de referencia: <strong>{formatPeriodo(icld.periodo)}</strong> —
              Art. 10 Ley 617/2000. Nota: Este indicador es informativo. Durante el último
              trimestre realizar seguimiento continuo al recaudo de ICLD.
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
              <tbody>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                    <strong>A</strong> — 1.5% del recaudo ICLD (fuente 11001)
                    <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                      Recaudo total: {formatCOP(icld.recaudo_11001)}
                    </div>
                  </td>
                  <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                    {formatCOP(icld.limite_icld_1_5)}
                  </td>
                </tr>
                <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
                    <strong>B</strong> — Presupuesto definitivo sección 18 (sin honorarios concejales)
                    <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                      Honorarios concejales sección 18: {formatCOP(icld.ppto_honorarios_concejo)}
                    </div>
                  </td>
                  <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                    {formatCOP(icld.ppto_sec18_sin_honorarios)}
                  </td>
                </tr>
                <tr style={{ background: icld.alerta ? "#fef2f2" : "#f0fdf4" }}>
                  <td style={{ padding: "0.75rem 0.5rem", fontWeight: 700, color: "#163b8c" }}>
                    Diferencia (A − B)
                    <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#6b7280", marginTop: "0.2rem" }}>
                      {icld.alerta
                        ? "⚠️ Valor negativo: se debe ajustar el presupuesto del concejo."
                        : "✅ Margen disponible para adicionar al presupuesto del concejo."}
                    </div>
                  </td>
                  <td style={{ padding: "0.75rem 0.5rem", textAlign: "right",
                    fontWeight: 800, fontSize: "1.15rem",
                    color: icld.alerta ? "#dc2626" : "#16a34a" }}>
                    {formatCOP(icld.diferencia)}
                  </td>
                </tr>
              </tbody>
            </table>
            {icld.alerta && (
              <div style={{ marginTop: "1rem", background: "#fef2f2", border: "1px solid #fca5a5",
                borderRadius: "10px", padding: "0.85rem 1rem", color: "#991b1b", fontSize: "0.9rem" }}>
                <strong>⚠️ Alerta:</strong> El presupuesto definitivo del concejo (sección 18) supera
                el límite legal. Se debe realizar una reducción presupuestal de{" "}
                <strong>{formatCOP(Math.abs(icld.diferencia))}</strong> para cumplir el indicador.
              </div>
            )}
          </div>
        )}

        {/* ── IPC por vigencia ── */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: mostrarIpc ? "1rem" : 0 }}>
            <h2 style={{ margin: 0, color: "#163b8c", fontSize: "1.15rem" }}>IPC por vigencia</h2>
            <button type="button" onClick={() => setMostrarIpc(!mostrarIpc)}
              style={{ background: "#f0f4ff", color: "#1d4ed8", border: "1px solid #c7d2fe",
                borderRadius: "8px", padding: "0.4rem 0.85rem",
                cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}>
              {mostrarIpc ? "Ocultar ▲" : "Gestionar ▼"}
            </button>
          </div>
          {mostrarIpc && (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", marginBottom: "1rem" }}>
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "left" }}>Vigencia</th>
                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>IPC (%)</th>
                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "left" }}>Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {ipcs.map(i => (
                    <tr key={i.vigencia} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600 }}>{i.vigencia}</td>
                      <td style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>
                        {parseFloat(i.ipc_pct).toFixed(2)}%
                      </td>
                      <td style={{ padding: "0.6rem 0.75rem", color: "#6b7280", fontSize: "0.8rem" }}>
                        {parseInt(i.vigencia) === 2025 ? "Base (no aplica IPC)" : `Aplicado para vigencia ${i.vigencia}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Vigencia</label>
                  <input type="number" value={nuevaVigIpc} min={2026}
                    onChange={e => setNuevaVigIpc(parseInt(e.target.value))} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>IPC (%)</label>
                  <input type="number" value={nuevoIpc} step="0.01" placeholder="ej: 5.10"
                    onChange={e => setNuevoIpc(e.target.value)} style={inputStyle} />
                </div>
                <button type="button" onClick={handleGuardarIpc}
                  style={{ background: "#059669", color: "#fff", border: "none",
                    borderRadius: "10px", padding: "0.5rem 1rem",
                    cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                  + Agregar IPC
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── Histórico ── */}
        {calculos.length > 0 && (
          <div style={card}>
            <h2 style={{ margin: "0 0 1rem", color: "#163b8c", fontSize: "1.15rem" }}>
              Histórico de cálculos guardados
            </h2>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "left" }}>Vigencia</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "left" }}>Categoría</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "right" }}>Concejales</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "right" }}>Valor sesión</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "right" }}>Ses. Ord.</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "right" }}>Ses. Ext.</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "right" }}>Total honorarios</th>
                    <th style={{ padding: "0.6rem 0.5rem", textAlign: "left" }}>Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {calculos.map(c => (
                    <tr key={c.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "0.55rem 0.5rem", fontWeight: 600 }}>{c.vigencia}</td>
                      <td style={{ padding: "0.55rem 0.5rem" }}>{c.categoria}</td>
                      <td style={{ padding: "0.55rem 0.5rem", textAlign: "right" }}>{c.num_concejales}</td>
                      <td style={{ padding: "0.55rem 0.5rem", textAlign: "right" }}>{formatCOP(c.valor_sesion_vigencia)}</td>
                      <td style={{ padding: "0.55rem 0.5rem", textAlign: "right" }}>{c.sesiones_ordinarias}</td>
                      <td style={{ padding: "0.55rem 0.5rem", textAlign: "right" }}>{c.sesiones_extraordinarias}</td>
                      <td style={{ padding: "0.55rem 0.5rem", textAlign: "right", fontWeight: 700, color: "#163b8c" }}>
                        {formatCOP(c.total_honorarios_vigencia)}
                      </td>
                      <td style={{ padding: "0.55rem 0.5rem", color: "#6b7280", fontSize: "0.8rem" }}>
                        {new Date(c.creado_en).toLocaleDateString("es-CO")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}